import { useCallback, useMemo, useState } from 'react';
import { Plus, LayoutTemplate, ClipboardList, Save, Trash2, Printer } from 'lucide-react';
import type { Event, PlanningItem } from '@peles/shared';
import { EntityList, FilterSelect } from '../common';
import type { EntityListColumn } from '../common';
import { EntityModal } from '../common/EntityModal';
import { AttachmentButton } from '../attachments/AttachmentButton';
import { useDialog } from '../../context/DialogContext';
import { useEvent } from '../../hooks/useEvents';
import { useExpenseItems } from '../../hooks/useExpenses';
import { useRooms } from '../../hooks/useFloorPlans';
import { useVendorCategories } from '../../hooks/useVendors';
import { useSettingsCatalogItems } from '../../hooks/useSettings';
import {
  usePlanningLists,
  usePlanningStatuses,
  useCreatePlanningList,
  useSavePlanningListAsTemplate,
  useDeletePlanningItem,
} from '../../hooks/usePlanning';
import { PlanningItemModal, expenseLineLabel } from './PlanningItemModal';
import { PlanningTemplatePickerModal } from './PlanningTemplatePickerModal';
import { ItemizedPrintModal } from './ItemizedPrintModal';
import type { ItemizedPrintFilter } from './ItemizedPrintModal';

/** Attachments on planning items use this polymorphic entity type. */
export const PLANNING_ITEM_ATTACHMENT_TYPE = 'PLANNING_ITEM';

export interface ItemizedListFilters {
  categoryIds?: string[];
  roomIds?: string[];
  vendorKeys?: string[];
  expenseItemIds?: string[];
}

interface ItemizedListViewProps {
  eventId: string;
  /** Pre-selected filters (e.g. zoom-in from an expense row). */
  initialFilters?: ItemizedListFilters;
}

type EventWithRelations = Event & {
  eventTypeId?: string;
  eventTypeName?: string;
  venueId?: string | null;
  venueName?: string;
  logoUrl?: string | null;
};

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function itemLabel(item: PlanningItem): string {
  return item.itemName ?? item.catalogItemName ?? '—';
}

/** Stable filter key for the (possibly id-less) vendor behind an expense line. */
function vendorKey(item: PlanningItem): string | null {
  return item.expenseVendorId ?? item.expenseVendorName ?? null;
}

/**
 * Itemized view of all planning items for an event, aggregated across its
 * planning lists, with multi-select filters by category, room, vendor
 * (resolved from the linked expense line), expense line, and status.
 */
export function ItemizedListView({ eventId, initialFilters }: ItemizedListViewProps) {
  const { data: event } = useEvent(eventId);
  const eventRel = event as EventWithRelations | undefined;
  const venueId = eventRel?.venueId ?? null;

  const { data: lists = [], isLoading } = usePlanningLists({ eventId });
  const { data: categories = [] } = useVendorCategories();
  const { data: catalogItems = [] } = useSettingsCatalogItems();
  const { data: statuses = [] } = usePlanningStatuses();
  const { data: rooms = [] } = useRooms(venueId ?? undefined, !!venueId);
  const { data: expenseItems = [] } = useExpenseItems({ eventId });
  const createList = useCreatePlanningList();
  const saveAsTemplate = useSavePlanningListAsTemplate();
  const deleteItem = useDeletePlanningItem();
  const { confirm } = useDialog();

  const [itemModal, setItemModal] = useState<{
    open: boolean;
    item: PlanningItem | null;
    listId: string | null;
  }>({ open: false, item: null, listId: null });
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string[]>(initialFilters?.categoryIds ?? []);
  const [roomFilter, setRoomFilter] = useState<string[]>(initialFilters?.roomIds ?? []);
  const [vendorFilter, setVendorFilter] = useState<string[]>(initialFilters?.vendorKeys ?? []);
  const [expenseFilter, setExpenseFilter] = useState<string[]>(
    initialFilters?.expenseItemIds ?? [],
  );

  // Zooming in from a single expense line seeds new rows with that expense.
  const defaultExpenseItemId =
    initialFilters?.expenseItemIds?.length === 1 ? initialFilters.expenseItemIds[0] : undefined;

  // All items for the event, across its planning lists.
  const items = useMemo(() => lists.flatMap((l) => l.items), [lists]);
  const primaryList = lists[0] ?? null;

  // Vendor options come from the vendors behind the items' expense lines.
  const vendorOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) {
      const key = vendorKey(item);
      if (key && !map.has(key)) map.set(key, item.expenseVendorName ?? 'Unknown vendor');
    }
    for (const expense of expenseItems) {
      const key = expense.vendorId ?? expense.vendorName;
      if (key && !map.has(key)) map.set(key, expense.vendorName ?? 'Unknown vendor');
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items, expenseItems]);

  const venueRooms = useMemo(
    () => rooms.filter((r) => r.venueId === venueId).sort((a, b) => a.name.localeCompare(b.name)),
    [rooms, venueId],
  );

  const filteredItems = useMemo(() => {
    let result = items;
    if (categoryFilter.length > 0) {
      result = result.filter(
        (i) => i.vendorCategoryId && categoryFilter.includes(i.vendorCategoryId),
      );
    }
    if (roomFilter.length > 0) {
      result = result.filter((i) => i.roomId && roomFilter.includes(i.roomId));
    }
    if (vendorFilter.length > 0) {
      result = result.filter((i) => {
        const key = vendorKey(i);
        return key !== null && vendorFilter.includes(key);
      });
    }
    if (expenseFilter.length > 0) {
      result = result.filter((i) => i.expenseItemId && expenseFilter.includes(i.expenseItemId));
    }
    return result;
  }, [items, categoryFilter, roomFilter, vendorFilter, expenseFilter]);

  const grandTotal = useMemo(
    () => filteredItems.reduce((sum, i) => sum + i.total, 0),
    [filteredItems],
  );

  // Human-readable summary of the active filters — shown on the printed report.
  const activeFilters: ItemizedPrintFilter[] = useMemo(
    () => [
      {
        label: 'Category',
        values: categoryFilter.map((id) => categories.find((c) => c.id === id)?.name ?? 'Unknown'),
      },
      {
        label: 'Room',
        values: roomFilter.map((id) => rooms.find((r) => r.id === id)?.name ?? 'Unknown'),
      },
      {
        label: 'Vendor',
        values: vendorFilter.map(
          (key) => vendorOptions.find((v) => v.value === key)?.label ?? 'Unknown',
        ),
      },
      {
        label: 'Expense',
        values: expenseFilter.map((id) => {
          const expense = expenseItems.find((e) => e.id === id);
          return expense ? expenseLineLabel(expense) : 'Unknown';
        }),
      },
    ],
    [
      categoryFilter,
      roomFilter,
      vendorFilter,
      expenseFilter,
      categories,
      rooms,
      vendorOptions,
      expenseItems,
    ],
  );

  const handleDeleteItem = useCallback(
    async (item: PlanningItem) => {
      if (
        !(await confirm({
          title: 'Delete Row',
          message: `Delete this row (${itemLabel(item)})? This cannot be undone.`,
          confirmLabel: 'Delete',
        }))
      )
        return;
      deleteItem.mutate(item.id);
    },
    [deleteItem, confirm],
  );

  // Adding a row auto-creates the event's planning list when none exists yet.
  const handleAddRow = useCallback(() => {
    if (primaryList) {
      setItemModal({ open: true, item: null, listId: primaryList.id });
      return;
    }
    createList.mutate(
      { eventId, eventTypeId: eventRel?.eventTypeId, title: 'Planning List' },
      { onSuccess: (created) => setItemModal({ open: true, item: null, listId: created.id }) },
    );
  }, [primaryList, createList, eventId, eventRel?.eventTypeId]);

  function handleSaveTemplate() {
    if (!primaryList || !templateName.trim()) return;
    saveAsTemplate.mutate(
      {
        id: primaryList.id,
        templateName: templateName.trim(),
        eventTypeId: eventRel?.eventTypeId,
      },
      {
        onSuccess: () => {
          setSaveTemplateOpen(false);
          setTemplateName('');
        },
      },
    );
  }

  const columns: EntityListColumn<PlanningItem>[] = [
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      sortValue: (i) => i.vendorCategoryName ?? '',
      render: (i) => <span className="text-gray-700">{i.vendorCategoryName ?? '—'}</span>,
    },
    {
      key: 'item',
      header: 'Item',
      sortable: true,
      sortValue: (i) => itemLabel(i),
      render: (i) => <span className="font-medium text-gray-900">{itemLabel(i)}</span>,
    },
    {
      key: 'room',
      header: 'Room',
      sortable: true,
      sortValue: (i) => i.roomName ?? '',
      render: (i) => <span className="text-gray-700">{i.roomName ?? '—'}</span>,
    },
    {
      key: 'qty',
      header: 'Qty',
      align: 'right',
      sortable: true,
      sortValue: (i) => i.quantity,
      render: (i) => i.quantity,
    },
    {
      key: 'unitPrice',
      header: 'Unit Price',
      align: 'right',
      sortable: true,
      sortValue: (i) => i.unitPrice,
      render: (i) => formatCurrency(i.unitPrice),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      sortable: true,
      sortValue: (i) => i.total,
      render: (i) => <span className="font-medium">{formatCurrency(i.total)}</span>,
    },
    {
      key: 'expense',
      header: 'Expense',
      sortable: true,
      sortValue: (i) => i.expenseItemName ?? '',
      render: (i) =>
        i.expenseItemId ? (
          <span className="text-gray-700">{i.expenseItemName ?? '—'}</span>
        ) : (
          <span className="text-amber-600 text-xs font-medium">Not linked</span>
        ),
    },
    {
      key: 'vendor',
      header: 'Vendor',
      sortable: true,
      sortValue: (i) => i.expenseVendorName ?? '',
      render: (i) => <span className="text-gray-700">{i.expenseVendorName ?? '—'}</span>,
    },
    {
      key: 'todos',
      header: 'Todos',
      align: 'center',
      sortable: true,
      sortValue: (i) => i.todos.length,
      render: (i) => {
        const open = i.todos.filter((t) => !t.isDone).length;
        if (i.todos.length === 0) return <span className="text-gray-300">—</span>;
        return (
          <span className="text-xs text-gray-600">
            {open}/{i.todos.length} open
          </span>
        );
      },
    },
    {
      key: 'attachments',
      header: '',
      align: 'right',
      render: (i) => (
        <AttachmentButton entityType={PLANNING_ITEM_ATTACHMENT_TYPE} entityId={i.id} />
      ),
    },
    {
      key: 'delete',
      header: '',
      align: 'right',
      render: (i) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteItem(i);
          }}
          disabled={deleteItem.isPending}
          className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
          aria-label={`Delete ${itemLabel(i)}`}
          title="Delete row"
        >
          <Trash2 size={16} />
        </button>
      ),
    },
  ];

  const filterBar = (
    <div className="flex flex-wrap items-center gap-2">
      <FilterSelect
        label="All Categories"
        items={categories}
        value={categoryFilter}
        onChange={(v) => setCategoryFilter(v as string[])}
        getValue={(c) => c.id}
        getLabel={(c) => c.name}
        multiple
      />
      {venueRooms.length > 0 && (
        <FilterSelect
          label="All Rooms"
          items={venueRooms}
          value={roomFilter}
          onChange={(v) => setRoomFilter(v as string[])}
          getValue={(r) => r.id}
          getLabel={(r) => r.name}
          multiple
        />
      )}
      <FilterSelect
        label="All Vendors"
        items={vendorOptions}
        value={vendorFilter}
        onChange={(v) => setVendorFilter(v as string[])}
        getValue={(o) => o.value}
        getLabel={(o) => o.label}
        multiple
      />
      <FilterSelect
        label="All Expenses"
        items={expenseItems}
        value={expenseFilter}
        onChange={(v) => setExpenseFilter(v as string[])}
        getValue={(e) => e.id}
        getLabel={(e) => expenseLineLabel(e)}
        multiple
      />
    </div>
  );

  // ─── Empty state ──────────────────
  if (!isLoading && items.length === 0 && lists.length === 0) {
    return (
      <>
        <div className="py-12 flex flex-col items-center gap-3 text-center">
          <ClipboardList size={40} className="text-gray-300" />
          <p className="text-sm text-gray-500">No itemized list yet for this event.</p>
          <div className="flex gap-2">
            <button
              onClick={handleAddRow}
              disabled={createList.isPending}
              className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              <Plus size={16} /> Add Item
            </button>
            <button
              onClick={() => setTemplatePickerOpen(true)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            >
              <LayoutTemplate size={16} /> Use Template
            </button>
          </div>
        </div>
        <PlanningTemplatePickerModal
          open={templatePickerOpen}
          onClose={() => setTemplatePickerOpen(false)}
          eventId={eventId}
          eventTypeId={eventRel?.eventTypeId}
        />
        {itemModal.open && itemModal.listId && (
          <PlanningItemModal
            key={`empty-${itemModal.listId}`}
            open={itemModal.open}
            onClose={() => setItemModal({ open: false, item: null, listId: null })}
            listId={itemModal.listId}
            item={null}
            eventId={eventId}
            venueId={venueId}
            defaultExpenseItemId={defaultExpenseItemId}
            categories={categories}
            catalogItems={catalogItems}
            statuses={statuses}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-base font-semibold text-gray-900">Itemized List</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPrintOpen(true)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            data-testid="itemized-print-open"
          >
            <Printer size={16} />
            <span className="hidden sm:inline">Print</span>
          </button>
          <button
            onClick={() => setTemplatePickerOpen(true)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            <LayoutTemplate size={16} />
            <span className="hidden sm:inline">Use Template</span>
          </button>
          {primaryList && (
            <button
              onClick={() => {
                setTemplateName(`${primaryList.title ?? 'Planning'} Template`);
                setSaveTemplateOpen(true);
              }}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
            >
              <Save size={16} />
              <span className="hidden sm:inline">Save as Template</span>
            </button>
          )}
          <button
            onClick={handleAddRow}
            disabled={createList.isPending}
            className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Add Item</span>
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="text-xs text-gray-500">Items</div>
          <div className="text-lg font-semibold text-gray-900">{filteredItems.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="text-xs text-gray-500">Estimated Cost</div>
          <div className="text-lg font-semibold text-gray-900">{formatCurrency(grandTotal)}</div>
        </div>
      </div>

      <EntityList
        items={filteredItems}
        columns={columns}
        getKey={(i) => i.id}
        isLoading={isLoading}
        onEdit={(i) => setItemModal({ open: true, item: i, listId: i.planningListId })}
        defaultSortBy="category"
        defaultSortDir="asc"
        filterBar={filterBar}
        emptyMessage="No items match the selected filters"
      />

      {itemModal.listId && (
        <PlanningItemModal
          // Remount per open so a fresh "Add" initialises defaults (e.g. the
          // "Searching" status) against the loaded statuses, with a clean form.
          key={`${itemModal.open ? 'open' : 'closed'}-${itemModal.item?.id ?? 'new'}`}
          open={itemModal.open}
          onClose={() => setItemModal({ open: false, item: null, listId: null })}
          listId={itemModal.listId}
          item={itemModal.item}
          eventId={eventId}
          venueId={venueId}
          defaultExpenseItemId={defaultExpenseItemId}
          categories={categories}
          catalogItems={catalogItems}
          statuses={statuses}
        />
      )}

      <PlanningTemplatePickerModal
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        eventId={eventId}
        eventTypeId={eventRel?.eventTypeId}
      />

      {printOpen && eventRel && (
        <ItemizedPrintModal
          event={eventRel}
          items={filteredItems}
          activeFilters={activeFilters}
          onClose={() => setPrintOpen(false)}
        />
      )}

      <EntityModal
        open={saveTemplateOpen}
        onClose={() => setSaveTemplateOpen(false)}
        title="Save as Template"
        size="sm"
        footer={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSaveTemplateOpen(false)}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveTemplate}
              disabled={!templateName.trim() || saveAsTemplate.isPending}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
              data-testid="save-template-submit"
            >
              {saveAsTemplate.isPending ? 'Saving…' : 'Save Template'}
            </button>
          </div>
        }
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </EntityModal>
    </>
  );
}
